import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Siren, TrendingUp, Users, Clock, Target, Plus, ArrowUpRight, MapPin, Loader2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { useLiveEvents } from '../lib/events';

const fmtR = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString('en-ZA');
const ago = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} d ago`;
};

const STATUS_BADGE = {
  open:        { label: 'Open',        cls: 'bg-red-100 text-red-700 border-red-200' },
  in_progress: { label: 'In Progress', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  closed:      { label: 'Closed',      cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const renderStatus = (s) => {
  const b = STATUS_BADGE[s] || { label: s || '—', cls: '' };
  return <Badge className={`${b.cls} hover:${b.cls}`}>{b.label}</Badge>;
};

const CONTRACTOR_BADGE = {
  'On Assignment': 'bg-blue-100 text-blue-700 border-blue-200',
  'Available':     'bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30',
};

const PIE_COLORS = ['#0D5B69', '#80C030', '#0EA5A5', '#F59E0B', '#EF4444', '#8B5CF6'];


export const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);

  const refetch = () => {
    api.get('/reports/dashboard')
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  useEffect(() => {
    let alive = true;
    api.get('/reports/dashboard')
      .then((r) => alive && setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // Live updates — refetch whenever an incident is created/updated/closed/cancelled/assigned.
  useLiveEvents(
    ['incident.created', 'incident.updated', 'incident.closed',
      'incident.cancelled', 'incident.reopened',
      'incident.assignment_added', 'incident.assignment_ended', 'connected'],
    (event) => {
      if (event.type === 'connected') { setLive(true); return; }
      refetch();
      if (event.type === 'incident.created' && event.payload?.incident_no) {
        toast.success(`New incident logged: ${event.payload.incident_no}`);
      }
    },
  );

  if (loading) {
    return (
      <Layout title="Dashboard" subtitle="Security Operations Overview">
        <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading live operations data…
        </div>
      </Layout>
    );
  }
  if (!data) return <Layout title="Dashboard" subtitle="—"><div /></Layout>;

  const k = data.kpis;
  const totalServiceIncidents = data.by_service.reduce((a, r) => a + r.incidents, 0) || 1;
  const pieData = data.by_service.map((r, i) => ({
    name: r.service_name,
    value: r.incidents,
    pct: Math.round((r.incidents / totalServiceIncidents) * 100),
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  const stats = [
    {
      title: 'Active Incidents',
      value: fmtInt(k.active_incidents),
      change: k.active_incidents_delta_vs_yesterday >= 0
        ? `+${k.active_incidents_delta_vs_yesterday} from yesterday`
        : `${k.active_incidents_delta_vs_yesterday} from yesterday`,
      icon: Siren, color: 'text-red-500', bg: 'bg-red-50',
      slaAtRisk: k.sla_at_risk || 0,
      slaBreached: k.sla_breached || 0,
      overdue: k.assignment_overdue || 0,
    },
    {
      title: 'Incidents Today',
      value: fmtInt(k.incidents_today),
      change: `${k.resolved_today} resolved`,
      icon: Clock, color: 'text-orange-500', bg: 'bg-orange-50',
    },
    {
      title: 'Revenue Today',
      value: fmtR(k.revenue_today),
      change: 'Incl. VAT',
      icon: TrendingUp, color: 'text-[#80C030]', bg: 'bg-[#80C030]/10',
    },
    {
      title: 'Active Contractors',
      value: fmtInt(k.active_contractors_total),
      change: `${k.active_contractors_on_assignment} on assignment`,
      icon: Users, color: 'text-blue-500', bg: 'bg-blue-50',
    },
  ];

  return (
    <Layout title="Dashboard" subtitle={live ? 'Security Operations Overview · Live' : 'Security Operations Overview'}>
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
        {/* Live indicator */}
        <div className="flex items-center gap-2 text-xs" data-testid="live-indicator">
          <span className={`relative flex h-2 w-2`}>
            <span className={`${live ? 'animate-ping bg-[#80C030]' : 'bg-slate-300'} absolute inline-flex h-full w-full rounded-full opacity-75`} />
            <span className={`${live ? 'bg-[#80C030]' : 'bg-slate-400'} relative inline-flex rounded-full h-2 w-2`} />
          </span>
          <span className="text-slate-500">{live ? 'Live feed connected — updates stream in real time' : 'Connecting to live feed…'}</span>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={i} className="bg-white border-slate-200" data-testid={`stat-card-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-500">{s.title}</p>
                    <p className="text-2xl font-bold font-heading text-slate-900 mt-1">{s.value}</p>
                    <p className="text-xs text-slate-400 mt-1">{s.change}</p>
                    {(s.slaAtRisk != null) && (s.slaAtRisk > 0 || s.slaBreached > 0 || s.overdue > 0) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="sla-radar-badges">
                        {s.overdue > 0 && (
                          <Badge className="bg-red-600 text-white border-red-700 text-[10px] font-bold hover:bg-red-600 gap-1 animate-pulse" data-testid="overdue-badge">
                            <span className="w-1.5 h-1.5 rounded-full bg-white" />
                            {s.overdue} no contractor!
                          </Badge>
                        )}
                        {s.slaBreached > 0 && (
                          <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px] font-bold hover:bg-red-100 gap-1 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                            {s.slaBreached} breached
                          </Badge>
                        )}
                        {s.slaAtRisk > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] font-semibold hover:bg-amber-100 gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            {s.slaAtRisk} at-risk
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={`${s.bg} p-2.5 rounded-lg shrink-0`}>
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Incidents */}
          <Card className="lg:col-span-2 bg-white border-slate-200" data-testid="recent-incidents-card">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg text-[#0D5B69]">Recent Incidents</CardTitle>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate('/occurrence-book')} data-testid="view-all-incidents">
                  View All <ArrowUpRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {data.recent_incidents.length === 0 ? (
                <p className="text-center text-slate-400 py-10">No incidents yet.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.recent_incidents.map((inc, idx) => (
                    <div key={inc.id} className="p-4 hover:bg-slate-50/80 transition-colors" data-testid={`incident-row-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#0D5B69]/10 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-[#0D5B69]" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate" data-testid={`incident-customer-${idx}`}>{inc.customer_name || '—'}</p>
                            <p className="text-sm text-[#0D5B69] font-medium truncate">{inc.service_name}</p>
                            <p className="text-[11px] text-slate-400 font-mono mt-0.5">{inc.incident_no}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {renderStatus(inc.status)}
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-400 justify-end">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate max-w-[180px]">{inc.location_address || '—'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end mt-2 pl-14">
                        <span className="text-xs text-slate-400">{ago(inc.opened_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Service Pie */}
          <Card className="bg-white border-slate-200" data-testid="incidents-by-type-card">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Incidents by Type</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-4">
                {pieData.length === 0 ? <p className="text-center text-xs text-slate-400 py-4">No data</p>
                  : pieData.map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 truncate">
                        <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: e.color }}></div>
                        <span className="text-slate-600 truncate">{e.name}</span>
                      </div>
                      <span className="font-medium text-slate-900">{e.value}</span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily trend */}
          <Card className="bg-white border-slate-200" data-testid="weekly-chart">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Last 7 Days — Incidents</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                    <Bar dataKey="incidents" fill="#0D5B69" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Contractors */}
          <Card className="bg-white border-slate-200" data-testid="contractors-card">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg text-[#0D5B69]">Active Contractors</CardTitle>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate('/contractors')} data-testid="manage-contractors">
                  Manage <ArrowUpRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {data.contractor_summary.length === 0 ? (
                <p className="text-center text-slate-400 py-10">No active contractors.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.contractor_summary.map((c, idx) => (
                    <div key={c.id} className="p-4 hover:bg-slate-50/80 transition-colors" data-testid={`contractor-row-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-semibold text-slate-600">
                              {c.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{c.name}</p>
                            <p className="text-xs text-slate-500">{c.area}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`${CONTRACTOR_BADGE[c.status] || ''} hover:opacity-100`}>{c.status}</Badge>
                          {c.active_incidents > 0 && (
                            <p className="text-xs text-slate-400 mt-1">{c.active_incidents} active</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick actions */}
        <Card className="bg-white border-slate-200" data-testid="quick-actions">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Quick Actions</h3>
              <div className="flex gap-3">
                <Button className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-2 font-semibold" onClick={() => navigate('/occurrence-book')} data-testid="new-incident-btn">
                  <Plus className="w-4 h-4" /> New Incident
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => navigate('/customers')} data-testid="add-customer-btn">
                  <Users className="w-4 h-4" /> Add Customer
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => navigate('/reports')} data-testid="generate-report-btn">
                  <TrendingUp className="w-4 h-4" /> Reports
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
